/**
 * Stock.jsx - Stock Management Page
 * 
 * TODO: REFACTORING NEEDED (Code Quality Review)
 * This component has grown to 1,000+ lines and should be split into:
 * - StockList: Main stock listing and filtering
 * - StockFilters: Search, shop filter, and filter controls
 * - StockActions: Bulk actions (delete, export)
 * - StockUpdateCard: The right-side stock update form
 * - StockImport: CSV import functionality
 * 
 * For now, the code is organized into logical sections.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, ScanBarcode, X, Package, Store, Check, Trash2, AlertTriangle, Calendar, Eye, ArrowLeftRight, Barcode, Plus, RefreshCw, Info, Layers, BarChart3 } from "lucide-react";
import BarcodeScannerDialog from "@/pages/BarcodeScannerDialog";
import ShopWiseStockView from "@/pages/stock/ShopWiseStockView";
import StockTransfer from "@/pages/stock/StockTransfer";
import ManagementTab from "@/pages/stock/ManagementTab";
import BarcodePrintingTab from "@/pages/stock/BarcodePrintingTab";
import StockRebalancer from "@/pages/stock/StockRebalancer";

// Smart date parser - handles various formats
const parseFlexibleDate = (input) => {
  if (!input || input.trim() === "") return null;
  
  const str = input.trim().toLowerCase();
  
  // Month name mapping
  const months = {
    jan: 1, january: 1,
    feb: 2, february: 2,
    mar: 3, march: 3,
    apr: 4, april: 4,
    may: 5,
    jun: 6, june: 6,
    jul: 7, july: 7,
    aug: 8, august: 8,
    sep: 9, sept: 9, september: 9,
    oct: 10, october: 10,
    nov: 11, november: 11,
    dec: 12, december: 12,
  };
  
  let day, month, year;
  
  // Try different patterns
  
  // Pattern: "16 mar 26" or "16 march 2026" or "16-mar-26"
  let match = str.match(/^(\d{1,2})[\s\-\/]+([a-z]+)[\s\-\/]+(\d{2,4})$/);
  if (match) {
    day = parseInt(match[1], 10);
    month = months[match[2]];
    year = parseInt(match[3], 10);
    if (month && day && year) {
      if (year < 100) year += 2000;
      return formatDate(year, month, day);
    }
  }
  
  // Pattern: "mar 16 26" or "march 16 2026"
  match = str.match(/^([a-z]+)[\s\-\/]+(\d{1,2})[\s\-\/]+(\d{2,4})$/);
  if (match) {
    month = months[match[1]];
    day = parseInt(match[2], 10);
    year = parseInt(match[3], 10);
    if (month && day && year) {
      if (year < 100) year += 2000;
      return formatDate(year, month, day);
    }
  }
  
  // Pattern: "16/03/26" or "16-03-26" or "16.03.26" (DD/MM/YY)
  match = str.match(/^(\d{1,2})[\s\-\/\.]+(\d{1,2})[\s\-\/\.]+(\d{2,4})$/);
  if (match) {
    day = parseInt(match[1], 10);
    month = parseInt(match[2], 10);
    year = parseInt(match[3], 10);
    if (day > 12 && month <= 12) {
      // Likely DD/MM/YY format
    } else if (month > 12 && day <= 12) {
      // Swap - likely MM/DD/YY entered
      [day, month] = [month, day];
    }
    if (year < 100) year += 2000;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return formatDate(year, month, day);
    }
  }
  
  // Pattern: "2026-03-16" (ISO format)
  match = str.match(/^(\d{4})[\s\-\/]+(\d{1,2})[\s\-\/]+(\d{1,2})$/);
  if (match) {
    year = parseInt(match[1], 10);
    month = parseInt(match[2], 10);
    day = parseInt(match[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return formatDate(year, month, day);
    }
  }
  
  // Pattern: "mar 26" or "march 2026" (assume day 1)
  match = str.match(/^([a-z]+)[\s\-\/]+(\d{2,4})$/);
  if (match) {
    month = months[match[1]];
    year = parseInt(match[2], 10);
    if (month && year) {
      if (year < 100) year += 2000;
      return formatDate(year, month, 1);
    }
  }
  
  // Pattern: "03/26" or "03-26" (MM/YY - assume day 1)
  match = str.match(/^(\d{1,2})[\s\-\/]+(\d{2,4})$/);
  if (match) {
    month = parseInt(match[1], 10);
    year = parseInt(match[2], 10);
    if (month >= 1 && month <= 12) {
      if (year < 100) year += 2000;
      return formatDate(year, month, 1);
    }
  }
  
  return null;
};

const formatDate = (year, month, day) => {
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
};

// Format parsed date for display
const formatParsedDateDisplay = (isoDate) => {
  if (!isoDate) return "";
  try {
    const [y, m, d] = isoDate.split("-");
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return isoDate;
  }
};

// Helper to format days until expiry
const formatExpiry = (days) => {
  if (days === null || days === undefined) return null;
  if (days < 0) {
    const absDays = Math.abs(days);
    if (absDays >= 30) {
      const months = Math.floor(absDays / 30);
      const remainingDays = absDays % 30;
      return `Expired ${months}m ${remainingDays}d ago`;
    }
    return `Expired ${absDays}d ago`;
  }
  if (days === 0) return "Expires today";
  if (days < 30) return `${days}d left`;
  const months = Math.floor(days / 30);
  const remainingDays = days % 30;
  if (remainingDays === 0) return `${months}m left`;
  return `${months}m ${remainingDays}d left`;
};

// Get expiry status color
const getExpiryClass = (status) => {
  switch (status) {
    case "expired": return "bg-red-100 text-red-800";
    case "expiring_soon": return "bg-orange-100 text-orange-800";
    case "expiring_3months": return "bg-yellow-100 text-yellow-800";
    case "ok": return "bg-emerald-50 text-emerald-700";
    default: return "bg-gray-100 text-gray-500";
  }
};

// Format expiry date as dd-mm-yy
const formatExpiryDate = (isoDate) => {
  if (!isoDate) return null;
  try {
    const parts = isoDate.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}-${month}-${year.slice(-2)}`;
    }
    return isoDate;
  } catch {
    return isoDate;
  }
};

export default function Stock() {
  const { user } = useAuth();
  const [mode, setMode] = useState("search"); // "search" | "scan"
  const [scanOpen, setScanOpen] = useState(false);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [quantity, setQuantity] = useState("");
  const [mrp, setMrp] = useState("");
  const [mrpNA, setMrpNA] = useState(false);
  const [expiryInput, setExpiryInput] = useState(""); // Raw user input
  const [expiryDate, setExpiryDate] = useState(""); // Parsed ISO date
  const [expiryNA, setExpiryNA] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productStock, setProductStock] = useState(null);
  const searchRef = useRef(null);
  const skipDebounceRef = useRef(false); // Skip debounced search after barcode scan
  
  // ADD/UPDATE mode state
  const [stockMode, setStockMode] = useState("add"); // "add" | "update"
  const [existingBatch, setExistingBatch] = useState(null); // Existing batch info if found
  const [checkingBatch, setCheckingBatch] = useState(false);
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false); // Confirmation dialog for UPDATE mode

  // Parse expiry date when input changes
  useEffect(() => {
    if (expiryNA || !expiryInput) {
      setExpiryDate("");
      return;
    }
    const parsed = parseFlexibleDate(expiryInput);
    setExpiryDate(parsed || "");
  }, [expiryInput, expiryNA]);

  // Fetch shops on mount and filter based on user access
  useEffect(() => {
    api.get("/shops").then((r) => {
      let allShops = r.data.items || [];
      
      // Filter shops based on user access (staff only see assigned shops, masters/super_users see all)
      const isMasterOrSuper = user?.role === "master" || user?.role === "super_user";
      const userShopIds = user?.shop_ids || [];
      
      if (!isMasterOrSuper && userShopIds.length > 0) {
        allShops = allShops.filter(s => userShopIds.includes(s.id));
      }
      
      setShops(allShops);
    }).catch(() => {});
  }, [user]);

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/products", { params: { q: query, limit: 10 } });
      setResults(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search - skip if barcode scan just completed
  useEffect(() => {
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }
    const t = setTimeout(() => searchProducts(q), 300);
    return () => clearTimeout(t);
  }, [q, searchProducts]);

  // Fetch stock for selected product
  const fetchProductStock = async (productId) => {
    try {
      const { data } = await api.get(`/stock/product/${productId}`);
      setProductStock(data);
    } catch (e) {
      console.error(e);
    }
  };

  // Check if batch exists with current parameters
  const checkExistingBatch = useCallback(async () => {
    if (!selectedProduct || !selectedShop) {
      setExistingBatch(null);
      return;
    }
    
    const mrpValue = mrpNA ? null : (mrp ? parseFloat(mrp) : null);
    const expiryValue = expiryNA ? null : (expiryDate || null);
    
    setCheckingBatch(true);
    try {
      const { data } = await api.post("/stock/check-batch", {
        product_id: selectedProduct.id,
        shop_id: selectedShop,
        mrp: mrpValue,
        expiry_date: expiryValue,
      });
      setExistingBatch(data.exists ? data : null);
      // Reset to ADD mode when batch check changes
      setStockMode("add");
    } catch (e) {
      console.error(e);
      setExistingBatch(null);
    } finally {
      setCheckingBatch(false);
    }
  }, [selectedProduct, selectedShop, mrp, mrpNA, expiryDate, expiryNA]);

  // Debounced batch check when relevant fields change
  useEffect(() => {
    const t = setTimeout(() => checkExistingBatch(), 300);
    return () => clearTimeout(t);
  }, [checkExistingBatch]);

  // Select a product
  const selectProduct = (product) => {
    setSelectedProduct(product);
    setResults([]);
    setQ("");
    setQuantity("");
    setMrp(product.mrp ? String(product.mrp) : "");
    setMrpNA(!product.mrp);
    setExpiryInput("");
    setExpiryDate("");
    setExpiryNA(false);
    setSelectedShop("");
    fetchProductStock(product.id);
  };

  // Handle barcode scan - SEARCH ONLY, do not auto-select
  const onBarcodeDetected = async (barcode) => {
    setScanOpen(false);
    setMode("search");
    skipDebounceRef.current = true; // Skip the debounced search that will trigger from setQ
    setQ(barcode);
    setSelectedProduct(null); // Clear any previous selection
    setSearching(true);
    
    try {
      // Search using the barcode - get ALL matching products, not just one
      const { data } = await api.get("/products", { params: { q: barcode, limit: 20 } });
      
      if (data.items && data.items.length > 0) {
        // Display results in dropdown - user must explicitly select
        setResults(data.items);
        toast.info(`Found ${data.items.length} product${data.items.length > 1 ? 's' : ''} for barcode "${barcode}". Please select one.`);
      } else {
        setResults([]);
        toast.error(`No products found for barcode "${barcode}"`);
      }
    } catch (e) {
      setResults([]);
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  // Update stock
  const updateStock = async (forceMode = null) => {
    if (!selectedProduct || !selectedShop || quantity === "") {
      toast.error("Please select product, shop and enter quantity");
      return;
    }
    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error("Quantity must be a valid non-negative number");
      return;
    }
    
    const mrpValue = mrpNA ? null : (mrp ? parseFloat(mrp) : null);
    const expiryValue = expiryNA ? null : (expiryDate || null);
    
    // Validate expiry input if provided but not parseable
    if (!expiryNA && expiryInput && !expiryDate) {
      toast.error("Could not parse expiry date. Try formats like: 16 mar 26, 16/03/26, mar 2026");
      return;
    }
    
    const modeToUse = forceMode || stockMode;
    
    // Show confirmation for UPDATE mode with existing batch
    if (modeToUse === "update" && existingBatch && !forceMode) {
      setUpdateConfirmOpen(true);
      return;
    }
    
    setSaving(true);
    try {
      const { data } = await api.post("/stock/update", {
        product_id: selectedProduct.id,
        shop_id: selectedShop,
        quantity: qty,
        mrp: mrpValue,
        expiry_date: expiryValue,
        mode: modeToUse,
      });
      
      // Show appropriate success message
      if (modeToUse === "add" && existingBatch) {
        toast.success(`Stock added! ${existingBatch.quantity} + ${qty} = ${data.new_quantity}`);
      } else if (modeToUse === "update" && existingBatch) {
        toast.success(`Stock replaced! ${existingBatch.quantity} → ${qty}`);
      } else {
        toast.success("Stock updated successfully");
      }
      
      fetchProductStock(selectedProduct.id);
      setQuantity("");
      setExpiryInput("");
      setExistingBatch(null);
      setStockMode("add");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setUpdateConfirmOpen(false);
    }
  };

  // Confirm UPDATE mode
  const confirmUpdate = () => {
    updateStock("update");
  };

  // Delete a batch
  const deleteBatch = async (batchId) => {
    if (!confirm("Delete this stock batch?")) return;
    try {
      await api.delete(`/stock/batch/${batchId}`);
      toast.success("Batch deleted");
      fetchProductStock(selectedProduct.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedProduct(null);
    setProductStock(null);
    setSelectedShop("");
    setQuantity("");
    setMrp("");
    setMrpNA(false);
    setExpiryInput("");
    setExpiryDate("");
    setExpiryNA(false);
    setExistingBatch(null);
    setStockMode("add");
  };

  // Get shop name
  const getShopName = (shopId) => shops.find(s => s.id === shopId)?.name || shopId;

  // Per-shop edit guard: staff can VIEW stock for any shop, but only DELETE / MODIFY
  // batches in shops they're explicitly assigned to. Master / super_user bypass.
  const isMasterOrSuper = user?.role === "master" || user?.role === "super_user";
  const userShopIds = user?.shop_ids || [];
  const canEditShop = (shopId) => isMasterOrSuper || userShopIds.includes(shopId);

  // State for active tab
  const [activeTab, setActiveTab] = useState("update");
  
  // State for transfer pre-fill from Rebalancer
  const [transferPrefill, setTransferPrefill] = useState(null);
  
  // Callback for Rebalancer to initiate a transfer
  const handleCreateTransferFromRebalancer = (recommendation) => {
    // Set pre-fill data and switch to transfer tab
    setTransferPrefill({
      productId: recommendation.product_id,
      productName: recommendation.product_name,
      sourceShopId: recommendation.source_shop_id,
      sourceShopName: recommendation.source_shop_name,
      destShopId: recommendation.destination_shop_id,
      destShopName: recommendation.destination_shop_name,
      recommendedQty: recommendation.recommended_qty,
      timestamp: Date.now(), // Force re-trigger even for same product
    });
    setActiveTab("transfer");
  };
  
  // Clear pre-fill after it's been used
  const clearTransferPrefill = () => {
    setTransferPrefill(null);
  };

  return (
    <div className="fade-up" data-testid="stock-page">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Stock Management</div>
      <h1 className="font-heading text-2xl sm:text-3xl tracking-tight">Stock</h1>
      <p className="text-muted-foreground text-xs sm:text-sm mt-1 mb-4 sm:mb-6">
        Manage stock batches, view shop-wise inventory, and transfer between shops.
      </p>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-4" data-testid="stock-tabs">
          <TabsTrigger value="update" className="text-xs sm:text-sm" data-testid="tab-update-stock">
            <Package className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Update/</span>Correct
          </TabsTrigger>
          <TabsTrigger value="view" className="text-xs sm:text-sm" data-testid="tab-shop-view">
            <Eye className="w-4 h-4 mr-1 sm:mr-2" />
            Shop<span className="hidden sm:inline">-wise</span> View
          </TabsTrigger>
          <TabsTrigger value="management" className="text-xs sm:text-sm" data-testid="tab-management">
            <Layers className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">MANAGEMENT</span><span className="sm:hidden">Mgmt</span>
          </TabsTrigger>
          <TabsTrigger value="transfer" className="text-xs sm:text-sm" data-testid="tab-transfer">
            <ArrowLeftRight className="w-4 h-4 mr-1 sm:mr-2" />
            Transfer
          </TabsTrigger>
          <TabsTrigger value="rebalancer" className="text-xs sm:text-sm" data-testid="tab-rebalancer">
            <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Rebalancer</span><span className="sm:hidden">Bal</span>
          </TabsTrigger>
          <TabsTrigger value="barcodes" className="text-xs sm:text-sm" data-testid="tab-barcode-printing">
            <Barcode className="w-4 h-4 mr-1 sm:mr-2" />
            Barcode<span className="hidden sm:inline"> Printing</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Update Stock (Original Functionality) */}
        <TabsContent value="update" className="mt-0">
          {/* Mode Toggle */}
          <Card className="p-3 sm:p-4 mb-6">
            <div className="flex gap-2 mb-4">
              <Button
                variant={mode === "scan" ? "default" : "outline"}
                onClick={() => { setMode("scan"); setScanOpen(true); }}
                style={mode === "scan" ? { backgroundColor: "#9E6D5B" } : {}}
                className="flex-1 sm:flex-none text-xs sm:text-sm px-3 sm:px-4"
                data-testid="stock-scan-btn"
              >
            <ScanBarcode className="w-4 h-4 mr-1.5 sm:mr-2" /> 
            <span className="hidden xs:inline">Scan</span> Barcode
          </Button>
          <Button
            variant={mode === "search" ? "default" : "outline"}
            onClick={() => setMode("search")}
            style={mode === "search" ? { backgroundColor: "#9E6D5B" } : {}}
            className="flex-1 sm:flex-none text-xs sm:text-sm px-3 sm:px-4"
            data-testid="stock-search-btn"
          >
            <Search className="w-4 h-4 mr-1.5 sm:mr-2" /> Search
          </Button>
        </div>

        {/* Search Box */}
        {mode === "search" && !selectedProduct && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              ref={searchRef}
              className="pl-10"
              placeholder="Search by manual barcode, auto barcode, product, group or brand..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="stock-search-input"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                Searching...
              </div>
            )}

            {/* Search Results Dropdown */}
            {results.length > 0 && (
              <Card className="absolute z-50 w-full mt-1 max-h-80 overflow-y-auto shadow-lg border-2 border-[#9E6D5B]/30">
                <div className="sticky top-0 bg-muted/95 px-3 py-2 border-b text-xs text-muted-foreground font-medium">
                  {results.length} product{results.length > 1 ? 's' : ''} found — click to select
                </div>
                {results.map((p) => (
                  <button
                    key={p.id}
                    className="w-full p-3 text-left hover:bg-[#9E6D5B]/10 border-b last:border-b-0 transition-colors"
                    onClick={() => selectProduct(p)}
                    data-testid={`stock-result-${p.id}`}
                  >
                    <div className="font-medium text-sm">{p.product}</div>
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                        {p.brand_name && <span>Brand: {p.brand_name}</span>}
                        {(p.group_name || p.category) && <span>• Group: {p.group_name || p.category}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-[10px]">
                        <span>Auto: {p.auto_barcode || '—'}</span>
                        {p.manual_barcode && <span>• Manual: {p.manual_barcode}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                        {p.mrp && <span>MRP: ₹{Number(p.mrp).toFixed(2)}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </Card>
            )}
          </div>
        )}
      </Card>

      {/* Selected Product */}
      {selectedProduct && (
        <Card className="p-4 sm:p-5 mb-6" data-testid="stock-selected-product">
          <div className="flex items-start justify-between gap-2 mb-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#9E6D5B]/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-[#9E6D5B]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-base sm:text-lg leading-tight break-words">{selectedProduct.product}</div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  {selectedProduct.brand_name} · {selectedProduct.auto_barcode}
                </div>
                {selectedProduct.mrp && (
                  <div className="text-xs sm:text-sm font-mono-data mt-0.5">Default MRP: ₹{Number(selectedProduct.mrp).toFixed(2)}</div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={clearSelection} className="flex-shrink-0 h-8 w-8 p-0" data-testid="stock-clear-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Stock Batches Table */}
          {productStock && productStock.shops && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Current Stock Batches</div>
              </div>
              
              {productStock.all_batches && productStock.all_batches.length > 0 ? (
                <>
                  {/* Desktop: Table */}
                  <div className="hidden md:block overflow-x-auto rounded-md border border-[hsl(var(--border))]">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap w-[120px]">Shop</th>
                          <th className="p-3 text-center text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap w-[70px]">Qty</th>
                          <th className="p-3 text-right text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap w-[90px]">MRP</th>
                          <th className="p-3 text-center text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap min-w-[130px]">Days to Expire</th>
                          <th className="p-3 text-center text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap w-[90px]">Expiry</th>
                          <th className="p-3 text-right text-xs uppercase tracking-wider text-muted-foreground w-[60px]"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {productStock.all_batches.map((batch) => (
                          <tr key={batch.id} className="border-t hover:bg-muted/20 transition-colors">
                            <td className="p-3 font-medium whitespace-nowrap">{batch.shop_name}</td>
                            <td className="p-3 text-center font-mono-data tabular-nums">{batch.quantity}</td>
                            <td className="p-3 text-right font-mono-data tabular-nums whitespace-nowrap">
                              {batch.mrp !== null ? `₹${Number(batch.mrp).toFixed(0)}` : <span className="text-muted-foreground">N/A</span>}
                            </td>
                            <td className="p-3 text-center">
                              {batch.expiry_date ? (
                                <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getExpiryClass(batch.expiry_status)}`}>
                                  {formatExpiry(batch.days_until_expiry)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">N/A</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {batch.expiry_date ? (
                                <span className="text-xs text-muted-foreground font-mono-data tabular-nums">
                                  {formatExpiryDate(batch.expiry_date)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">N/A</span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {canEditShop(batch.shop_id) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteBatch(batch.id)}
                                  className="h-7 w-7 p-0"
                                  data-testid={`stock-batch-delete-${batch.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                </Button>
                              ) : (
                                <span
                                  className="inline-flex items-center justify-center h-7 w-7 text-muted-foreground/40"
                                  title="You don't have access to this shop"
                                  data-testid={`stock-batch-locked-${batch.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Card list */}
                  <div className="md:hidden space-y-2" data-testid="stock-batches-mobile">
                    {productStock.all_batches.map((batch) => (
                      <div
                        key={batch.id}
                        className="rounded-md border border-[hsl(var(--border))] p-3 bg-white"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Store className="w-3.5 h-3.5 text-[#9E6D5B] shrink-0" />
                              <span className="font-medium text-sm truncate">{batch.shop_name}</span>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Qty</div>
                                <div className="font-mono-data font-semibold mt-0.5 tabular-nums">{batch.quantity}</div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">MRP</div>
                                <div className="font-mono-data font-semibold mt-0.5 tabular-nums">
                                  {batch.mrp !== null ? `₹${Number(batch.mrp).toFixed(0)}` : <span className="text-muted-foreground font-normal">N/A</span>}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Days to Expire</div>
                                <div className="mt-0.5">
                                  {batch.expiry_date ? (
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${getExpiryClass(batch.expiry_status)}`}>
                                      {formatExpiry(batch.days_until_expiry)}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground text-xs">N/A</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expiry</div>
                                <div className="font-mono-data text-muted-foreground mt-0.5 tabular-nums text-xs">
                                  {batch.expiry_date ? formatExpiryDate(batch.expiry_date) : "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                          {canEditShop(batch.shop_id) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteBatch(batch.id)}
                              className="h-8 w-8 p-0 shrink-0"
                              data-testid={`stock-batch-delete-mobile-${batch.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          ) : (
                            <span
                              className="inline-flex items-center justify-center h-8 w-8 shrink-0 text-muted-foreground/40"
                              title="You don't have access to this shop"
                              data-testid={`stock-batch-locked-mobile-${batch.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-md">
                  No stock batches recorded yet. Add stock below.
                </div>
              )}

              {/* Shop Totals Summary */}
              <div className="mt-3 flex flex-wrap gap-2">
                {productStock.shops.map((s) => (
                  <div
                    key={s.shop_id}
                    className={`px-3 py-1.5 rounded-md text-sm ${
                      s.total_quantity !== null
                        ? s.total_quantity > 0
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <span className="font-medium">{s.shop_name}</span>
                    <span className="mx-1">-</span>
                    <span className="font-mono-data">
                      {s.total_quantity !== null ? s.total_quantity : "N/A"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stock Update Form */}
          <div className="border-t pt-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Add / Update Stock Batch</div>
            
            {/* Responsive form grid: 1 col mobile → 2 col sm → 4 col lg */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Shop */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Shop *</Label>
                <Select value={selectedShop} onValueChange={setSelectedShop}>
                  <SelectTrigger className="mt-1 w-full" data-testid="stock-shop-select">
                    <SelectValue placeholder="Select shop" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="max-h-60 min-w-[200px]">
                    {shops.map((shop) => (
                      <SelectItem key={shop.id} value={shop.id}>
                        {shop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quantity *</Label>
                <Input
                  type="number"
                  min="0"
                  className="mt-1 font-mono-data"
                  placeholder="Enter qty"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  data-testid="stock-quantity-input"
                />
              </div>

              {/* MRP */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">MRP</Label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <Checkbox
                      checked={mrpNA}
                      onCheckedChange={(v) => {
                        setMrpNA(!!v);
                        if (v) setMrp("");
                      }}
                    />
                    N/A
                  </label>
                </div>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="mt-1 font-mono-data"
                  placeholder="₹0.00"
                  value={mrp}
                  onChange={(e) => setMrp(e.target.value)}
                  disabled={mrpNA}
                  data-testid="stock-mrp-input"
                />
              </div>

              {/* Expiry Date */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Expiry</Label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <Checkbox
                      checked={expiryNA}
                      onCheckedChange={(v) => {
                        setExpiryNA(!!v);
                        if (v) {
                          setExpiryInput("");
                          setExpiryDate("");
                        }
                      }}
                    />
                    N/A
                  </label>
                </div>
                <div className="relative">
                  <Input
                    type="text"
                    className="mt-1 font-mono-data pr-20 sm:pr-24"
                    placeholder="e.g. 16 mar 26"
                    value={expiryInput}
                    onChange={(e) => setExpiryInput(e.target.value)}
                    disabled={expiryNA}
                    data-testid="stock-expiry-input"
                  />
                  {expiryDate && !expiryNA && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] sm:text-xs text-emerald-600 font-medium bg-emerald-50 px-1.5 sm:px-2 py-0.5 rounded">
                      {formatParsedDateDisplay(expiryDate)}
                    </div>
                  )}
                  {expiryInput && !expiryDate && !expiryNA && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-red-500">
                      Invalid
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Formats: 16 mar 26, 16/03/26, mar 2026
                </div>
              </div>
            </div>

            {/* Existing Batch Detection & ADD/UPDATE Mode Selector */}
            {existingBatch && (
              <Card className="mt-4 p-4 border-2 border-amber-200 bg-amber-50/50" data-testid="existing-batch-card">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <Info className="w-4 h-4 text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-800">Existing Batch Detected</div>
                    <div className="text-xs text-amber-700 mt-1">
                      This product already has a batch with the same MRP and expiry in this shop.
                    </div>
                    <div className="mt-2 p-2 bg-white rounded-lg border border-amber-200">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Current Qty:</span>
                          <span className="ml-2 font-semibold text-lg text-amber-800">{existingBatch.quantity}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Entered Qty:</span>
                          <span className="ml-2 font-semibold text-lg text-foreground">{quantity || 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Mode Selection */}
                    <div className="mt-3 space-y-2">
                      <div className="text-xs font-medium text-amber-800 uppercase tracking-wider">Choose Action:</div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {/* ADD Mode Button */}
                        <button
                          type="button"
                          onClick={() => setStockMode("add")}
                          className={`flex-1 p-3 rounded-lg border-2 transition-all text-left ${
                            stockMode === "add"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-[hsl(var(--border))] bg-white hover:border-emerald-300"
                          }`}
                          data-testid="stock-mode-add"
                        >
                          <div className="flex items-center gap-2">
                            <Plus className={`w-4 h-4 ${stockMode === "add" ? "text-emerald-600" : "text-muted-foreground"}`} />
                            <span className={`font-semibold text-sm ${stockMode === "add" ? "text-emerald-700" : "text-foreground"}`}>
                              ADD TO EXISTING STOCK
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            New Qty = {existingBatch.quantity} + {quantity || 0} = <span className="font-semibold text-emerald-700">{existingBatch.quantity + (parseInt(quantity) || 0)}</span>
                          </div>
                        </button>
                        
                        {/* UPDATE Mode Button - Show only if user has permission */}
                        {(() => {
                          const isMasterOrSuperUser = user?.role === "master" || user?.role === "super_user";
                          const stockLocked = existingBatch?.stock_lock;
                          const canUpdate = isMasterOrSuperUser || !stockLocked;
                          
                          if (!canUpdate) return null;
                          
                          return (
                            <button
                              type="button"
                              onClick={() => setStockMode("update")}
                              className={`flex-1 p-3 rounded-lg border-2 transition-all text-left ${
                                stockMode === "update"
                                  ? "border-red-500 bg-red-50"
                                  : "border-[hsl(var(--border))] bg-white hover:border-red-300"
                              }`}
                              data-testid="stock-mode-update"
                            >
                              <div className="flex items-center gap-2">
                                <RefreshCw className={`w-4 h-4 ${stockMode === "update" ? "text-red-600" : "text-muted-foreground"}`} />
                                <span className={`font-semibold text-sm ${stockMode === "update" ? "text-red-700" : "text-foreground"}`}>
                                  UPDATE ACTUAL STOCK
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                Replace: {existingBatch.quantity} → <span className="font-semibold text-red-700">{quantity || 0}</span>
                              </div>
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Loading state for batch check */}
            {checkingBatch && (
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                Checking existing batches...
              </div>
            )}

            {/* Submit Button — full width on mobile, auto-right on desktop */}
            <div className="mt-4 flex sm:justify-end">
              <Button
                className={`w-full sm:w-auto sm:min-w-[180px] text-white ${
                  existingBatch && stockMode === "update" ? "bg-red-600 hover:bg-red-700" : ""
                }`}
                onClick={() => updateStock()}
                disabled={saving || !selectedShop || quantity === ""}
                style={existingBatch && stockMode === "update" ? {} : { backgroundColor: "#9E6D5B" }}
                data-testid="stock-update-btn"
              >
                {existingBatch && stockMode === "add" && (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    {saving ? "Adding..." : "Add to Stock"}
                  </>
                )}
                {existingBatch && stockMode === "update" && (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {saving ? "Updating..." : "Replace Stock"}
                  </>
                )}
                {!existingBatch && (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Add Stock"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* UPDATE Confirmation Dialog */}
      <Dialog open={updateConfirmOpen} onOpenChange={setUpdateConfirmOpen}>
        <DialogContent data-testid="update-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirm Stock Replacement
            </DialogTitle>
            <DialogDescription>
              You are about to replace the existing quantity. This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Card className="p-4 border-red-200 bg-red-50">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Product:</span>
                  <span className="font-semibold">{selectedProduct?.product}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shop:</span>
                  <span className="font-semibold">{getShopName(selectedShop)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Current Quantity:</span>
                  <span className="font-semibold text-lg text-amber-700">{existingBatch?.quantity || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">New Quantity:</span>
                  <span className="font-semibold text-lg text-red-700">{quantity || 0}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-red-200">
                  <span className="text-muted-foreground">Difference:</span>
                  <span className={`font-semibold text-lg ${(parseInt(quantity) || 0) - (existingBatch?.quantity || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {(parseInt(quantity) || 0) - (existingBatch?.quantity || 0) >= 0 ? "+" : ""}{(parseInt(quantity) || 0) - (existingBatch?.quantity || 0)}
                  </span>
                </div>
              </div>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmUpdate} 
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? "Updating..." : "Confirm Replace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empty State */}
      {!selectedProduct && (
        <Card className="p-10 text-center text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Scan a barcode or search for a product to update its stock.</p>
        </Card>
      )}
        </TabsContent>

        {/* Tab: Shop-wise Stock View */}
        <TabsContent value="view" className="mt-0">
          <ShopWiseStockView shops={shops} />
        </TabsContent>

        {/* Tab: Stock Transfer */}
        <TabsContent value="transfer" className="mt-0">
          <StockTransfer 
            shops={shops} 
            prefill={transferPrefill}
            onPrefillConsumed={clearTransferPrefill}
          />
        </TabsContent>

        {/* Tab: Management (Phase 1 — Restock Queue + Manual Requests) */}
        <TabsContent value="management" className="mt-0">
          <ManagementTab shops={shops} />
        </TabsContent>

        {/* Tab: Rebalancer — Smart Stock Rebalancer Dashboard */}
        <TabsContent value="rebalancer" className="mt-0">
          <StockRebalancer 
            shops={shops}
            onCreateTransfer={handleCreateTransferFromRebalancer}
          />
        </TabsContent>

        {/* Tab: Barcode Printing — reuses BarcodePrintDialog for the print itself. */}
        <TabsContent value="barcodes" className="mt-0">
          <BarcodePrintingTab />
        </TabsContent>
      </Tabs>

      {/* Barcode Scanner Dialog */}
      <BarcodeScannerDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onDetected={onBarcodeDetected}
      />
    </div>
  );
}
